import React, {useCallback, useEffect, useMemo, useState} from "react";
import ContentSearchSection from "../../../../../../components/search-sections/content";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Api from "../../../../../../../core/services/api/api";
import {Paper, Typography} from "@mui/material";
import ContentBlog from "../../../../../../components/app-specific/content/blog";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import {DefaultPaginationInfo} from "../../../../../../../core/constants/enums";
import useSearch from "../../../../../../hooks/use-search";
import useInfiniteScrollSearchUtils from "../../../../../../hooks/use-infinite-scroll-search-utils";
import InfiniteScroll from "react-infinite-scroller";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";

const ContentBlogListView = () => {
    const [data, setData] = useState([]);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('content-blogs-list-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.contentBlogsList);
        configureBehavioralTool((tags) => tags.contentBlogsList);
    }, [configureBehavioralTool, configureNotebookTags]);
    /**
     * Searches for list of data for exporting or for showing the list of items in the view
     *
     * * Calls the api regarding this action and updated the pagination info and sets the [reports] value in the inner state if not for
     * exporting Otherwise, returns the crud response.
     * @type {function(*=, boolean=): Promise<*>}
     */
    const fetchEntries = useCallback(async (args) => {
        const forApi = {
            paginationInfo: args.paginationInfo,
            filters: {
                categoryIds: args.filters?.categories?.map(e => e.id),
                keyword: args.filters?.keywords?.toString(),
            },
        };
        const response = await Api.searchBlogs(forApi, args.cancelTokenSource?.token);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => args.paginationInfo.currentPage === 1 ? response.data.resultList ?? [] : [...prevState, ...response.data.resultList]);
            args.setPaginationInfo(prevState => ({
                ...prevState,
                length: (response.data.paginationInfo.totalCount) ?? 0,
            }));
        }
    }, [isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);

    const initialPaginationInfo = useMemo(() => DefaultPaginationInfo, [])

    const initialSearchDataSettings = useMemo(() => ({
        paginationInfo: initialPaginationInfo,
        filters: {
            categories: [],
            keywords: '',
        },
    }), [initialPaginationInfo]);

    const searchData = useSearch(search, initialSearchDataSettings, false)

    const {loadMoreData, hasMoreToLoad} = useInfiniteScrollSearchUtils({searchData, dataLength: data.length});

    return (
        <>
            <Typography variant={'subtitle1'} fontWeight={600}>
                Blogs of the system
            </Typography>
            <ContentSearchSection searchData={searchData} type={'Blog'}/>
            <div className={'px-2 content-blogs-list'}>
                <Paper className={'paper w-100 mb-3 p-4'}>
                    <InfiniteScroll
                        pageStart={0}
                        loadMore={loadMoreData}
                        hasMore={hasMoreToLoad}
                        useWindow={false}
                        getScrollParent={() => document.querySelector('main')}
                        threshold={50}
                    >
                        <div className={'d-flex flex-wrap'}>
                            {
                                data?.map(e => (
                                    <ContentBlog key={e.id} data={e}/>
                                ))
                            }
                            {
                                searchData.loading &&
                                Array(6).fill(null).map((_, index) => (
                                    <div key={index} className={'loading blog-loading'}>
                                        <div/>
                                    </div>
                                ))
                            }
                        </div>
                    </InfiniteScroll>
                    {
                        !searchData.loading && data?.length < 1 &&
                        <div className={'w-100 my-5 py-5'}>
                            <Typography variant={'body2'} textAlign={'center'}>
                                There are no blogs to show at the
                                moment {searchData.filters?.categories?.length > 0 || searchData?.filters?.keywords?.length ? 'for the selected filters' : ''}.
                            </Typography>
                        </div>
                    }
                </Paper>
            </div>
        </>
    )
}

export default ContentBlogListView;
